import {
  createTemplateVariant,
  deleteTemplateVariant,
  getTemplateVariant,
  listTemplateVariants,
  replaceTemplateVariantFile,
  updateTemplateVariant,
  type ContractGenerationTemplateVariantCreateRequest,
  type ContractGenerationTemplateVariantInDb,
  type ContractGenerationTemplateVariantSummary,
  type ContractGenerationTemplateVariantUpdate,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';

/**
 * A template's variants — the named editions of one template ("German",
 * "Freelancer (Germany)", "Male wording"). Each is its own .docx with its own
 * extracted schema; the name is free text the author chooses, unique within
 * the template, and nothing reads it to decide behaviour.
 *
 * Every variant is addressed by its own uid, never by its name — names are
 * renameable, uids are not.
 */
export const TemplateVariantsService = {
  /**
   * List endpoint returns a slim summary view (no `extracted_schema`) so the
   * overview UI can render lots of variants cheaply. Call `get()` on a
   * specific variant to retrieve the full `InDb` view including the schema.
   */
  async list(templateUid: string): Promise<ContractGenerationTemplateVariantSummary[]> {
    try {
      const response = await listTemplateVariants({
        path: { template_uid: templateUid },
      });
      if (!response.data) {
        throw new Error('Failed to list template variants');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to list template variants.'));
    }
  },

  async get(
    templateUid: string,
    variantUid: string
  ): Promise<ContractGenerationTemplateVariantInDb> {
    try {
      const response = await getTemplateVariant({
        path: { template_uid: templateUid, variant_uid: variantUid },
      });
      if (!response.data) {
        throw new Error('Failed to fetch template variant');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch template variant.'));
    }
  },

  /**
   * Add a variant to an existing template from a .docx, sent with the request.
   */
  async create(
    templateUid: string,
    file: File,
    name: string
  ): Promise<ContractGenerationTemplateVariantInDb> {
    try {
      const body: ContractGenerationTemplateVariantCreateRequest = {
        file,
        name,
      };

      const response = await createTemplateVariant({
        path: { template_uid: templateUid },
        body,
      });
      if (!response.data) {
        throw new Error('Failed to create template variant');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create template variant.'));
    }
  },

  /**
   * "Upload fixed version": the .docx becomes a new revision of the variant's
   * source, and the variant comes back re-validating.
   */
  async replaceFile(
    templateUid: string,
    variantUid: string,
    file: File
  ): Promise<ContractGenerationTemplateVariantInDb> {
    try {
      const response = await replaceTemplateVariantFile({
        path: { template_uid: templateUid, variant_uid: variantUid },
        body: { file },
      });
      if (!response.data) {
        throw new Error('Failed to upload the new version');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to upload the new version.'));
    }
  },

  /**
   * Rename a variant and/or change its display file name. Both fields are
   * optional — pass only what's changing. A name already used by another
   * variant of the same template is refused by the backend.
   */
  async update(
    templateUid: string,
    variantUid: string,
    payload: ContractGenerationTemplateVariantUpdate
  ): Promise<ContractGenerationTemplateVariantInDb> {
    try {
      const response = await updateTemplateVariant({
        path: { template_uid: templateUid, variant_uid: variantUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to update template variant');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update template variant.'));
    }
  },

  /**
   * Delete a variant by its uid.
   * Returns the deleted record so callers can confirm what was removed.
   * The drafts made from the variant go with it; saved documents stay.
   */
  async delete(
    templateUid: string,
    variantUid: string
  ): Promise<ContractGenerationTemplateVariantInDb> {
    try {
      const response = await deleteTemplateVariant({
        path: { template_uid: templateUid, variant_uid: variantUid },
      });
      if (!response.data) {
        throw new Error('Failed to delete template variant');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete template variant.'));
    }
  },
};
